import { Injectable, OnDestroy } from '@angular/core';
import { Event, NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  NOT_TRANSLATED_SUB_PAGES,
  PAGES_WITHOUT_TRANSLATION,
} from '@core/api/constants/not-translated-pages.constants';
import { RoutingService } from '@core/services/routing.service';
import { BehaviorSubject, filter, Observable, Subject, takeUntil } from 'rxjs';

import { AVAILABLE_LANGS, DEFAULT_LANG, TranslateLang } from '../constants/available-langs';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageRoutingService implements OnDestroy {
  private isPageWithoutTranslationSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  public isPageWithoutTranslation$: Observable<boolean> =
    this.isPageWithoutTranslationSubject$.asObservable();

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private routingService: RoutingService,
  ) {
    this.init();
  }

  private init(): void {
    this.router.events
      .pipe(
        filter(
          (event: Event | RouterEvent): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateUrlForActiveLang();
        this.setIsPageWithoutTranslation(this.router.url);
      });
  }

  public updateUrlForActiveLang(): void {
    const [url, queryParams] = this.routingService.getCurrentRoute().split('?');
    const urlSegments: string[] = url.split('/').filter(segment => segment);
    const activeLang: string = this.languageService.activeLang;

    if (AVAILABLE_LANGS.includes(urlSegments[0] as TranslateLang)) {
      urlSegments.splice(0, 1);
    }

    if (!url.match(`/${activeLang}/`)) {
      this.navigateWithLang(urlSegments, queryParams, activeLang);
    }
  }

  private navigateWithLang(segments: string[], queryParams: string, activeLang: string): void {
    queryParams ||= '';
    const path: string = [...PAGES_WITHOUT_TRANSLATION, ...NOT_TRANSLATED_SUB_PAGES].some(
      (page: string): boolean => this.routingService.getCurrentRoute().includes(page),
    )
      ? this.createUrl([...segments], queryParams)
      : this.getNewPath(segments, queryParams, activeLang);

    this.router.navigateByUrl(path, { replaceUrl: true });
  }

  private getNewPath(segments: string[], queryParams: string, activeLang: string): string {
    return activeLang === DEFAULT_LANG
      ? this.createUrl([...segments], queryParams)
      : this.createUrl([activeLang, ...segments], queryParams);
  }

  private createUrl(host: string[], queryParams: string): string {
    return queryParams ? `${host.join('/')}?${queryParams}` : `${host.join('/')}`;
  }

  private setIsPageWithoutTranslation(url: string): void {
    const isDocumentation: boolean = PAGES_WITHOUT_TRANSLATION.some((path: string) =>
      url?.includes(`/${path}`),
    );
    this.isPageWithoutTranslationSubject$.next(isDocumentation);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
